/**
 * Change History Tab - Paginated audit log for this center
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import locationService, { ChangeLogEntry } from '../../services/api/locationService';

interface ChangeHistoryTabProps {
  locationId: string;
}

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Timeline = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TimelineItem = styled.li`
  position: relative;
  padding-left: ${({ theme }) => theme.spacing[8]};
  padding-bottom: ${({ theme }) => theme.spacing[6]};

  &::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 20px;
    bottom: 0;
    width: 2px;
    background: ${({ theme }) => theme.colors.borders.light};
  }

  &:last-child::before {
    display: none;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 0;
  top: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 2px solid ${({ theme }) => theme.colors.primary.sky};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DotInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.sky};
`;

const EntryCard = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const EntryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const EntryAction = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: capitalize;
`;

const EntryMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
`;

const EntryUser = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EntryDate = styled.time`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  white-space: nowrap;
`;

const DiffList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const DiffRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const DiffField = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 80px;
  text-transform: capitalize;
`;

const DiffOld = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  background: ${({ theme }) => theme.colors.status.error.bg};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: monospace;
`;

const DiffNew = styled.span`
  color: ${({ theme }) => theme.colors.status.success.text};
  background: ${({ theme }) => theme.colors.status.success.bg};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: monospace;
`;

const DiffArrow = styled.span`
  color: ${({ theme }) => theme.colors.text.light};
  display: flex;
  align-items: center;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// ==================== Helpers ====================

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const formatAction = (action: string) =>
  action.toLowerCase().replace(/_/g, ' ');

// ==================== Component ====================

export const ChangeHistoryTab = ({ locationId }: ChangeHistoryTabProps) => {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['location-changelog', locationId, page],
    queryFn: () => locationService.getLocationChangelog(locationId, { page, limit: 15 }),
  });

  const entries = data?.data ?? [];
  const meta = data?.meta;

  return (
    <Container>
      <SectionTitle>Change History</SectionTitle>

      {isLoading ? (
        <Timeline>
          {Array.from({ length: 4 }).map((_, i) => (
            <TimelineItem key={i}>
              <TimelineDot>
                <DotInner />
              </TimelineDot>
              <EntryCard>
                <EntryHeader>
                  <SkeletonBar $width="120px" />
                  <SkeletonBar $width="80px" />
                </EntryHeader>
              </EntryCard>
            </TimelineItem>
          ))}
        </Timeline>
      ) : entries.length === 0 ? (
        <EmptyState>No change history recorded for this center.</EmptyState>
      ) : (
        <Timeline>
          {entries.map((entry: ChangeLogEntry) => (
            <TimelineItem key={entry.id}>
              <TimelineDot>
                <DotInner />
              </TimelineDot>
              <EntryCard>
                <EntryHeader>
                  <EntryAction>{formatAction(entry.action)}</EntryAction>
                  <EntryMeta>
                    {entry.userName && <EntryUser>by {entry.userName}</EntryUser>}
                    <EntryDate dateTime={entry.createdAt}>
                      {formatDateTime(entry.createdAt)}
                    </EntryDate>
                  </EntryMeta>
                </EntryHeader>

                {entry.details?.fieldDiffs && entry.details.fieldDiffs.length > 0 && (
                  <DiffList>
                    {entry.details.fieldDiffs.map((diff, idx) => (
                      <DiffRow key={idx}>
                        <DiffField>{diff.field}</DiffField>
                        <DiffOld>{diff.oldValue || '(empty)'}</DiffOld>
                        <DiffArrow>
                          <ArrowRight />
                        </DiffArrow>
                        <DiffNew>{diff.newValue || '(empty)'}</DiffNew>
                      </DiffRow>
                    ))}
                  </DiffList>
                )}
              </EntryCard>
            </TimelineItem>
          ))}
        </Timeline>
      )}

      {meta && meta.totalPages > 1 && (
        <Pagination>
          <PaginationInfo>
            Page {meta.page} of {meta.totalPages} ({meta.total} entries)
          </PaginationInfo>
          <PaginationControls>
            <PageButton
              onClick={() => setPage((p) => p - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </PageButton>
            <PageButton
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= meta.totalPages}
              aria-label="Next page"
            >
              <ChevronRight />
            </PageButton>
          </PaginationControls>
        </Pagination>
      )}
    </Container>
  );
};
